import React, { useState, useEffect } from 'react';
import { ArrowRight, Sparkles, Mic, Bot, Target } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const HeroSection = () => {
  const { t } = useTranslation();
  const [displayedLines, setDisplayedLines] = useState([]);
  const [currentLineIdx, setCurrentLineIdx] = useState(0);
  const [currentCharIdx, setCurrentCharIdx] = useState(0);
  const [isTyping] = useState(true);

  const demoSteps = [
    { type: 'user',    text: t('hero.demo.s1') },
    { type: 'ai',      text: t('hero.demo.s2') },
    { type: 'success', text: t('hero.demo.s3') },
    { type: 'success', text: t('hero.demo.s4') },
    { type: 'success', text: t('hero.demo.s5') },
    { type: 'system',  text: t('hero.demo.s6') },
    { type: 'ai',      text: t('hero.demo.s7') },
    { type: 'success', text: t('hero.demo.s8') },
  ];

  useEffect(() => {
    if (!isTyping) return;

    if (currentLineIdx >= demoSteps.length) {
      const resetTimer = setTimeout(() => {
        setDisplayedLines([]);
        setCurrentLineIdx(0);
        setCurrentCharIdx(0);
      }, 3000);
      return () => clearTimeout(resetTimer);
    }

    const currentStep = demoSteps[currentLineIdx];
    const fullText = currentStep.text;

    if (currentCharIdx <= fullText.length) {
      const speed = currentCharIdx === 0 ? 400 : 25;
      const timer = setTimeout(() => {
        if (currentCharIdx === fullText.length) {
          setDisplayedLines(prev => [...prev, { ...currentStep, text: fullText }]);
          setCurrentLineIdx(prev => prev + 1);
          setCurrentCharIdx(0);
        } else {
          setCurrentCharIdx(prev => prev + 1);
        }
      }, speed);
      return () => clearTimeout(timer);
    }
  }, [currentLineIdx, currentCharIdx, isTyping, demoSteps]);

  return (
    <section className="relative min-h-screen hero-bg flex items-center" data-testid="hero-section">
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        {/* Left - Text */}
        <div className="animate-fade-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 mb-6">
            <Sparkles className="w-4 h-4 text-[#8B5CF6]" />
            <span className="text-sm text-[#c4b5fd]">{t('hero.badge')}</span>
          </div>

          <h1 className="font-['Outfit'] text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter mb-6 leading-[1.05]">
            {t('hero.title1')}
            <span className="block font-medium text-gradient">{t('hero.title2')}</span>
          </h1>

          <p className="text-lg text-[#A1A1AA] max-w-xl mb-6 leading-relaxed">
            {t('hero.subtitle')}
          </p>

          {/* 3 USP cards inline */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-[#0055FF]/40 transition-colors">
              <Mic className="w-5 h-5 text-[#8B5CF6] mb-2" />
              <div className="text-sm font-semibold text-white mb-1">{t('hero.usp.brandVoice')}</div>
              <div className="text-xs text-[#A1A1AA] leading-snug">{t('hero.usp.brandVoiceDesc')}</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-[#0055FF]/40 transition-colors">
              <Bot className="w-5 h-5 text-[#0055FF] mb-2" />
              <div className="text-sm font-semibold text-white mb-1">{t('hero.usp.autoPilot')}</div>
              <div className="text-xs text-[#A1A1AA] leading-snug">{t('hero.usp.autoPilotDesc')}</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-[#0055FF]/40 transition-colors">
              <Target className="w-5 h-5 text-[#10B981] mb-2" />
              <div className="text-sm font-semibold text-white mb-1">{t('hero.usp.engines')}</div>
              <div className="text-xs text-[#A1A1AA] leading-snug">{t('hero.usp.enginesDesc')}</div>
            </div>
          </div>

          {/* New feature chips (3.32 → 3.35.7) */}
          <div className="flex flex-wrap gap-2 mb-8" data-testid="hero-feature-chips">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[#06B6D4]/10 border border-[#06B6D4]/30 text-[#67e8f9]">
              {t('hero.chips.aiTracking')}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 text-[#c4b5fd]">
              {t('hero.chips.calendar')}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#fcd34d]">
              {t('hero.chips.imageSeo')}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[#10B981]/10 border border-[#10B981]/30 text-[#6ee7b7]">
              {t('hero.chips.refund')}
            </span>
          </div>

          <div className="flex flex-wrap gap-4">
            <a href="#demo">
              <Button
                className="bg-[#0055FF] hover:bg-[#3377FF] text-white rounded-full px-8 py-6 text-lg font-medium group"
                data-testid="hero-cta-primary"
              >
                {t('hero.ctaPrimary')}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
            <Link to="/register">
              <Button
                variant="outline"
                className="border-white/20 text-white hover:bg-white/5 rounded-full px-8 py-6 text-lg"
                data-testid="hero-cta-trial"
              >
                {t('hero.ctaSecondary')}
              </Button>
            </Link>
          </div>
          <p className="mt-4 text-sm text-[#71717A]">
            {t('hero.ctaNote')}
          </p>

          <div className="mt-12 flex items-center gap-8 flex-wrap">
            <div>
              <div className="text-3xl font-['Outfit'] font-semibold">8</div>
              <div className="text-sm text-[#A1A1AA]">{t('hero.stats.engines')}</div>
              <div className="text-xs text-[#555]">{t('hero.stats.enginesDetail')}</div>
            </div>
            <div className="w-px h-12 bg-white/10 hidden sm:block"></div>
            <div>
              <div className="text-3xl font-['Outfit'] font-semibold">21</div>
              <div className="text-sm text-[#A1A1AA]">{t('hero.stats.features')}</div>
              <div className="text-xs text-[#555]">{t('hero.stats.featuresDetail')}</div>
            </div>
            <div className="w-px h-12 bg-white/10 hidden sm:block"></div>
            <div>
              <div className="text-3xl font-['Outfit'] font-semibold">5</div>
              <div className="text-sm text-[#A1A1AA]">{t('hero.stats.languages')}</div>
              <div className="text-xs text-[#555]">{t('hero.stats.languagesDetail')}</div>
            </div>
          </div>
        </div>

        {/* Right - Interactive Demo */}
        <div className="animate-fade-up stagger-2" data-testid="hero-demo">
          <div className="demo-terminal glow-primary">
            <div className="demo-terminal-header">
              <div className="demo-terminal-dot bg-[#EF4444]"></div>
              <div className="demo-terminal-dot bg-[#F59E0B]"></div>
              <div className="demo-terminal-dot bg-[#10B981]"></div>
              <span className="ml-4 text-xs text-[#A1A1AA]">AEO Orchestra · {t('hero.demo.title')}</span>
            </div>
            <div className="demo-terminal-content min-h-[300px]">
              {displayedLines.map((step, index) => (
                <div key={index} className={`mb-3 text-sm ${step.type === 'user' ? 'text-[#0055FF]' : step.type === 'success' ? 'text-[#10B981]' : step.type === 'system' ? 'text-[#F59E0B]' : 'text-white'}`}>
                  {step.type === 'user' && <span className="text-[#A1A1AA]">&#8594; </span>}
                  {step.type === 'ai' && <span className="text-[#00E5FF]">AI: </span>}
                  {step.text}
                </div>
              ))}
              {currentLineIdx < demoSteps.length && (
                <div className={`mb-3 text-sm ${demoSteps[currentLineIdx].type === 'user' ? 'text-[#0055FF]' : demoSteps[currentLineIdx].type === 'success' ? 'text-[#10B981]' : demoSteps[currentLineIdx].type === 'system' ? 'text-[#F59E0B]' : 'text-white'}`}>
                  {demoSteps[currentLineIdx].type === 'user' && <span className="text-[#A1A1AA]">&#8594; </span>}
                  {demoSteps[currentLineIdx].type === 'ai' && <span className="text-[#00E5FF]">AI: </span>}
                  {demoSteps[currentLineIdx].text.slice(0, currentCharIdx)}
                  <span className="inline-block w-1.5 h-4 bg-[#0055FF] animate-pulse ml-0.5 align-middle"></span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#0055FF]/20 rounded-full blur-[128px] pointer-events-none"></div>
      <div className="absolute bottom-1/4 left-0 w-64 h-64 bg-[#8B5CF6]/10 rounded-full blur-[100px] pointer-events-none"></div>
    </section>
  );
};

export default HeroSection;
